import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  TextField,
  Box,
  Stack,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  CircularProgress,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Popover,
  Divider
} from '@mui/material';
import {
  Close,
  QrCodeScanner,
  Delete,
  AddBoxOutlined,
  Print,
  Upload,
  Inventory,
  Storage,
  MoreHoriz,
  Info
} from '@mui/icons-material';
import { DataService } from '../../services/data-service';
import { useAppDispatch } from 'src/redux/hooks';
import { showAlert } from 'src/redux/alert/alertReducer';
import ExcelImportDialog, { ImportRow } from '../ExcelImportDialog';
import { bulkBarcodeService } from '../../services/bulk-barcode-service';

export interface BoxItem {
  unitId: string;
  barcode: string;
  itemName: string;
  quantity: number;
  // For reallocation/moveOut contexts
  searchResult?: any;
  availableLocations?: InventoryLocation[];
  selectedSource?: {
    storageUnitId: string;
    storageUnitName: string;
    boxBarcode?: string;
    availableQuantity: number;
  };
}

export interface BoxDetails {
  barcode: string;
  storageUnitId: string;
  storageUnitName: string;
  items: BoxItem[];
}

export interface GenerateBoxesDialogProps {
  open: boolean;
  onClose: () => void;
  onGenerate: (count: number) => void;
  loading?: boolean;
  title?: string;
  description?: string;
}

export interface InventoryLocation {
  storageUnitId: string;
  storageUnitName: string;
  storageUnitCode: string;
  boxes: Array<{boxBarcode: string; quantity: number}>;
  independentQuantity?: number;
  totalQuantity: number;
  availableQuantity: number;
}

export interface EnhancedBoxScanDialogProps {
  open: boolean;
  onClose: () => void;
  boxBarcode: string;
  boxDetails: BoxDetails | null;
  onItemsConfirm: (items: BoxItem[]) => void;
  onPrintLabel?: () => void;
  importing?: boolean;
  context?: 'moveIn' | 'moveOut' | 'reallocation';
  // For source selection in reallocation/moveOut
  storageUnits?: Record<string, string>;
  availableBoxes?: Record<string, Record<string, string>>;
  onLoadBoxesForStorageUnit?: (storageUnitId: string) => Promise<Record<string, string>>;
}

// Common Generate Boxes Dialog
export const GenerateBoxesDialog: React.FC<GenerateBoxesDialogProps> = ({
  open,
  onClose,
  onGenerate,
  loading = false,
  title = "Generate Boxes",
  description = "This will generate new boxes with unique barcodes that can be scanned and managed like regular boxes."
}) => {
  const [count, setCount] = useState('');
  const dispatch = useAppDispatch();

  const handleGenerate = () => {
    const boxCount = parseInt(count);
    if (!boxCount || boxCount <= 0 || boxCount > 100) {
      dispatch(showAlert({ 
        message: 'Please enter a valid number of boxes (1-100)', 
        type: 'warning' 
      }));
      return;
    }
    onGenerate(boxCount);
    setCount('');
    onClose();
  };

  const handleClose = () => {
    setCount('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">{title}</Typography>
          <IconButton onClick={handleClose}><Close /></IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
          
          <TextField
            fullWidth
            label="Number of Boxes"
            type="number"
            value={count}
            onChange={(e) => setCount(e.target.value)}
            inputProps={{ min: 1, max: 100 }}
            placeholder="Enter number of boxes to generate"
            autoFocus
            onKeyPress={(e) => { 
              if (e.key === 'Enter' && count) handleGenerate(); 
            }}
          />
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button 
          variant="contained" 
          onClick={handleGenerate}
          disabled={!count || loading}
          startIcon={loading ? <CircularProgress size={20} /> : <AddBoxOutlined />}
        >
          {loading ? 'Generating...' : 'Generate Boxes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Enhanced Box Scan Dialog with Import functionality
export const EnhancedBoxScanDialog: React.FC<EnhancedBoxScanDialogProps> = ({
  open,
  onClose,
  boxBarcode,
  boxDetails,
  onItemsConfirm,
  onPrintLabel,
  importing = false,
  context = 'moveIn',
  storageUnits = {},
  availableBoxes = {},
  onLoadBoxesForStorageUnit
}) => {
  const [scannedItems, setScannedItems] = useState<BoxItem[]>([]);
  const [currentScan, setCurrentScan] = useState('');
  const [scanning, setScanning] = useState(false);
  const [boxImportDialogOpen, setBoxImportDialogOpen] = useState(false);
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (open && boxDetails) {
      setScannedItems([...boxDetails.items]);
    } else if (open) {
      setScannedItems([]);
    }
  }, [open, boxDetails]);

  const searchItemBarcode = async (barcode: string) => {
    if (!barcode.trim()) return;

    setScanning(true);
    try {
      // Use bulk search service for single barcode for consistency
      const searchResults = await bulkBarcodeService.searchBarcodes([barcode], {
        includeLocations: context !== 'moveIn',
        includeBoxes: context !== 'moveIn'
      });

      const result = searchResults[0];
      const itemName = result?.found && result.itemName 
        ? result.itemName 
        : `Unknown Unit (${barcode})`;
      const unitId = result?.found && result.unitId 
        ? result.unitId 
        : barcode;
      const isUnknown = !result?.found;

      // For reallocation context: only allow items that are in inventory
      if (context === 'reallocation' && isUnknown) {
        dispatch(showAlert({
          message: `${barcode} is not found or not in inventory. Cannot add to reallocation box.`,
          type: 'error'
        }));
        setCurrentScan('');
        return;
      }
      
      if (context === 'reallocation' && result?.found && (!result.locations || result.locations.length === 0)) {
        dispatch(showAlert({
          message: `${itemName} is known but not in inventory. Cannot add to reallocation box.`,
          type: 'error'
        }));
        setCurrentScan('');
        return;
      }

      const existingItemIndex = scannedItems.findIndex(item => item.barcode === barcode);
      if (existingItemIndex >= 0) {
        const existingItem = scannedItems[existingItemIndex];
        const maxAvailable = existingItem.selectedSource?.availableQuantity || 
          (existingItem.availableLocations?.[0]?.totalQuantity) || 0;
        
        if (existingItem.quantity >= maxAvailable) {
          dispatch(showAlert({
            message: `Cannot add more ${itemName}. Maximum available quantity (${maxAvailable}) already reached.`,
            type: 'warning'
          }));
          setCurrentScan('');
          return;
        }
        
        const updatedItems = [...scannedItems];
        updatedItems[existingItemIndex].quantity += 1;
        setScannedItems(updatedItems);
      } else {
        const availableLocs = result?.locations?.filter((loc: any) => (loc.totalQuantity || 0) > 0) || [];

        // Auto-select source if only one location available
        let autoSelectedSource: BoxItem['selectedSource'] = undefined;
        if (context !== 'moveIn' && availableLocs.length === 1) {
          const location = availableLocs[0];
          autoSelectedSource = {
            storageUnitId: location.storageUnitId,
            storageUnitName: location.storageUnitName,
            availableQuantity: location.totalQuantity
          };

          // Auto-select box if only one box exists
          if (location.boxes && location.boxes.length === 1) {
            const box = location.boxes[0];
            if (box.quantity > 0) {
              autoSelectedSource.boxBarcode = box.boxBarcode;
              autoSelectedSource.availableQuantity = box.quantity;
            }
          } else if (location.independentQuantity && location.independentQuantity > 0) {
            // If only independent quantity available, select independent
            autoSelectedSource.boxBarcode = 'independent';
            autoSelectedSource.availableQuantity = location.independentQuantity;
          }
        }

        const newItem: BoxItem = {
          unitId,
          barcode,
          itemName,
          quantity: 1,
          // For reallocation/moveOut contexts, store search result for source selection
          ...(context !== 'moveIn' && result?.found && result.locations && {
            searchResult: {
              type: result.type || 'unit',
              locations: availableLocs,
              itemName: result.itemName,
              unitId: result.unitId
            },
            availableLocations: availableLocs,
            selectedSource: autoSelectedSource
          })
        };
        setScannedItems(prev => [...prev, newItem]);
      }

      setCurrentScan('');

      const contextMessage = context === 'moveIn' 
        ? 'It will be created in the system.'
        : context === 'reallocation'
        ? 'Select source location in the table below.'
        : 'Select source location in the table below.';

      dispatch(showAlert({
        message: isUnknown 
          ? `Added unknown unit: ${barcode}. ${contextMessage}` 
          : `Added: ${itemName}${context !== 'moveIn' && result?.locations ? '. Select source location.' : ''}`,
        type: isUnknown ? 'warning' : 'success'
      }));
    } catch (error: any) {
      const newItem: BoxItem = {
        unitId: barcode,
        barcode,
        itemName: `Unknown Unit (${barcode})`,
        quantity: 1
      };

      const existingItemIndex = scannedItems.findIndex(item => item.barcode === barcode);
      if (existingItemIndex >= 0) {
        const updatedItems = [...scannedItems];
        updatedItems[existingItemIndex].quantity += 1;
        setScannedItems(updatedItems);
      } else {
        setScannedItems(prev => [...prev, newItem]);
      }

      setCurrentScan('');
      dispatch(showAlert({
        message: `Added unknown unit: ${barcode}. It will be created in the system.`,
        type: 'warning'
      }));
    } finally {
      setScanning(false);
    }
  };

  const handleScanSubmit = () => {
    if (currentScan.trim()) {
      searchItemBarcode(currentScan);
    }
  };

  const updateItemQuantity = (barcode: string, quantity: number) => {
    setScannedItems(prev => prev.map(item =>
      item.barcode === barcode ? { ...item, quantity: Math.max(1, quantity) } : item
    ));
  };

  const removeItem = (barcode: string) => {
    setScannedItems(prev => prev.filter(item => item.barcode !== barcode));
  };

  const handleConfirm = () => {
    if (scannedItems.length === 0) {
      dispatch(showAlert({ message: 'Please scan at least one item', type: 'warning' }));
      return;
    }
    onItemsConfirm(scannedItems);
    onClose();
  };

  const handleImportItems = async (importRows: ImportRow[]) => {
    if (!importRows.length) return;

    try {
      // Aggregate quantities by barcode
      const qtyByBarcode = new Map<string, number>();
      for (const row of importRows) {
        const barcode = (row.barcode || '').trim();
        if (!barcode) continue;
        const quantity = Math.max(1, parseInt(String(row.quantity)) || 1);
        qtyByBarcode.set(barcode, (qtyByBarcode.get(barcode) || 0) + quantity);
      }

      const barcodes = [...qtyByBarcode.keys()];
      if (!barcodes.length) return;

      // Use bulk search service for better performance
      const searchResults = await bulkBarcodeService.searchBarcodes(barcodes, {
        includeLocations: context !== 'moveIn',
        includeBoxes: context !== 'moveIn'
      });

      const processedItems: BoxItem[] = [];
      let knownCount = 0;
      let unknownCount = 0;

      for (const result of searchResults) {
        const quantity = qtyByBarcode.get(result.barcode) || 1;
        const itemName = result.found && result.itemName 
          ? result.itemName 
          : `Unknown Unit (${result.barcode})`;
        const unitId = result.found && result.unitId 
          ? result.unitId 
          : result.barcode;

        // For reallocation context: skip items that are not in inventory
        if (context === 'reallocation' && (!result.found || !result.locations || result.locations.length === 0)) {
          unknownCount++;
          continue;
        }
        
        if (result.found) {
          knownCount++;
        } else {
          unknownCount++;
        }

        // Check if item already exists in scanned items
        const existingItemIndex = scannedItems.findIndex(item => item.barcode === result.barcode);
        if (existingItemIndex >= 0) {
          // Update existing item quantity
          const updatedItems = [...scannedItems];
          updatedItems[existingItemIndex].quantity += quantity;
          setScannedItems(updatedItems);
        } else {
          // Create new item with search result data for reallocation/moveOut contexts
          const newItem: BoxItem = {
            unitId,
            barcode: result.barcode,
            itemName,
            quantity,
            // For reallocation/moveOut contexts, store search result for source selection
            ...(context !== 'moveIn' && result.found && result.locations && {
              searchResult: {
                type: result.type || 'unit',
                locations: result.locations.filter((loc: any) => (loc.totalQuantity || 0) > 0),
                itemName: result.itemName,
                unitId: result.unitId
              },
              availableLocations: result.locations.filter((loc: any) => (loc.totalQuantity || 0) > 0)
            })
          };
          processedItems.push(newItem);
        }
      }
      
      if (processedItems.length > 0) {
        setScannedItems(prev => [...prev, ...processedItems]);
      }
      
      const message = context === 'reallocation'
        ? unknownCount > 0
          ? `Imported ${knownCount} in-inventory items. ${unknownCount} items not in inventory were skipped.`
          : `Imported ${knownCount} in-inventory items.`
        : unknownCount > 0 
          ? `Imported ${knownCount} known items and ${unknownCount} unknown items` 
          : `Imported ${knownCount} items`;
          
      dispatch(showAlert({
        message,
        type: knownCount > 0 ? 'success' : 'warning'
      }));
      
    } catch (error: any) {
      dispatch(showAlert({ 
        message: error.message || 'Failed to import items', 
        type: 'error' 
      }));
    }
  };

  const getContextMessage = () => {
    switch (context) {
      case 'moveOut':
        return 'Scan individual items for this box. Items will be moved out with the box.';
      case 'reallocation':
        return 'Scan individual items for this box. Items will be reallocated with the box.';
      default:
        return 'Scan individual items in this box or import from Excel. Items will be automatically moved to the box\'s storage location.';
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">Box Contents - {boxBarcode}</Typography>
          <IconButton onClick={onClose}><Close /></IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2}>
          {/* Box Actions */}
          <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
            {onPrintLabel && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<Print />}
                onClick={onPrintLabel}
              >
                Print Label
              </Button>
            )}
            <Button
              variant="outlined"
              size="small"
              startIcon={<Upload />}
              onClick={() => setBoxImportDialogOpen(true)}
              disabled={importing}
            >
              {importing ? 'Importing...' : 'Import Items'}
            </Button>
          </Stack>

          <Alert severity="info">
            <Typography variant="body2">
              {getContextMessage()}
            </Typography>
          </Alert>

          <TextField
            fullWidth
            label="Scan Item Barcode"
            value={currentScan}
            onChange={(e) => {
              const value = e.target.value;
              // Prevent box barcode entry (barcodes starting with BOX- or containing GEN-BOX)
              if (value.startsWith('BOX-') || value.includes('GEN-BOX')) {
                dispatch(showAlert({
                  message: 'Cannot scan box barcodes in item scanner. Use box management features instead.',
                  type: 'warning'
                }));
                return;
              }
              setCurrentScan(value);
            }}
            onKeyPress={(e) => { if (e.key === 'Enter') handleScanSubmit(); }}
            onBlur={() => handleScanSubmit()}
            InputProps={{
              startAdornment: <QrCodeScanner sx={{ mr: 1, color: 'action.active' }} />,
              endAdornment: scanning && <CircularProgress size={20} />
            }}
            disabled={scanning}
            placeholder="Scan or enter item barcode (not box barcodes)"
          />

          {scannedItems.length > 0 ? (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width={140}>Barcode</TableCell>
                    <TableCell width={180}>Item Details</TableCell>
                    <TableCell width={120}>More Props</TableCell> 
                    {context !== 'moveIn' && <TableCell width={160}>Source</TableCell>}
                    {context !== 'moveIn' && <TableCell width={120}>Box/Location</TableCell>}
                    <TableCell width={80}>Quantity</TableCell>
                    <TableCell width={100}>Status</TableCell>
                    <TableCell width={80}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {scannedItems.map((item) => {
                    const isUnknown = item.itemName.includes('Unknown Unit');
                    return (
                      <TableRow key={item.barcode}>
                        <TableCell>
                          <Typography variant="body2" fontFamily="monospace">{item.barcode}</Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ py: 0.5 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>{item.itemName}</Typography>
                            {isUnknown && (
                              <Typography variant="caption" color="warning.main" sx={{ display: 'block', mb: 0.5 }}>
                                Will be created as new unit
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        {/* More Props Column */}
                        <TableCell width={120}>
                          {!isUnknown ? (
                            <AllPropertiesCell row={item} />
                          ) : (
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>-</Typography>
                          )}
                        </TableCell>
                        {/* Source Selection for reallocation/moveOut */}
                        {context !== 'moveIn' && (
                          <TableCell>
                            {item.availableLocations && item.availableLocations.length > 0 ? (
                              <FormControl size="small" fullWidth>
                                <InputLabel>Source</InputLabel>
                                <Select
                                  value={item.selectedSource?.storageUnitId || ''}
                                  onChange={(e) => {
                                    const location = item.availableLocations!.find(loc => loc.storageUnitId === e.target.value);
                                    if (location) {
                                      const updatedItems = scannedItems.map(i => 
                                        i.barcode === item.barcode 
                                          ? { 
                                              ...i, 
                                              selectedSource: {
                                                storageUnitId: location.storageUnitId,
                                                storageUnitName: location.storageUnitName,
                                                availableQuantity: location.totalQuantity
                                              }
                                            }
                                          : i
                                      );
                                      setScannedItems(updatedItems);
                                      if (onLoadBoxesForStorageUnit) {
                                        onLoadBoxesForStorageUnit(location.storageUnitId);
                                      }
                                    }
                                  }}
                                  label="Source"
                                >
                                  {item.availableLocations.map((location) => (
                                    <MenuItem key={location.storageUnitId} value={location.storageUnitId}>
                                      <Stack direction="row" alignItems="center" spacing={1}>
                                        <Storage fontSize="small" />
                                        <Typography variant="caption">{location.storageUnitName}</Typography>
                                      </Stack>
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            ) : (
                              <Typography variant="caption" color="text.secondary">
                                {isUnknown ? 'New item' : 'No inventory'}
                              </Typography>
                            )}
                          </TableCell>
                        )}
                        {/* Box/Location Selection for reallocation/moveOut */}
                        {context !== 'moveIn' && (
                          <TableCell>
                            {item.selectedSource && item.availableLocations ? (
                              (() => {
                                const location = item.availableLocations.find(loc => loc.storageUnitId === item.selectedSource!.storageUnitId);
                                const boxes = location?.boxes || [];
                                const hasIndependent = (location?.independentQuantity || 0) > 0;
                                
                                if (boxes.length === 0 && !hasIndependent) {
                                  return <Typography variant="caption" color="text.secondary">No stock</Typography>;
                                }
                                
                                const options = [
                                  ...(hasIndependent ? [{ value: '', label: 'Independent', quantity: location!.independentQuantity! }] : []),
                                  ...boxes.map(box => ({ value: box.boxBarcode, label: box.boxBarcode, quantity: box.quantity }))
                                ];
                                
                                if (options.length === 1) {
                                  return <Chip label={options[0].label || 'Independent'} size="small" variant="outlined" />;
                                }
                                
                                return (
                                  <FormControl size="small" fullWidth>
                                    <InputLabel>Box/Location</InputLabel>
                                    <Select
                                      value={item.selectedSource.boxBarcode || ''}
                                      onChange={(e) => {
                                        const selectedOption = options.find(opt => opt.value === e.target.value);
                                        if (selectedOption) {
                                          const updatedItems = scannedItems.map(i => 
                                            i.barcode === item.barcode 
                                              ? { 
                                                  ...i, 
                                                  selectedSource: {
                                                    ...i.selectedSource!,
                                                    boxBarcode: selectedOption.value,
                                                    availableQuantity: selectedOption.quantity
                                                  }
                                                }
                                              : i
                                          );
                                          setScannedItems(updatedItems);
                                        }
                                      }}
                                      label="Box/Location"
                                    >
                                      {options.map((option) => (
                                        <MenuItem key={option.value || 'independent'} value={option.value}>
                                          <Stack direction="row" alignItems="center" spacing={1}>
                                            {option.value === '' ? (
                                              <Inventory fontSize="small" color="action" />
                                            ) : (
                                              <Storage fontSize="small" />
                                            )}
                                            <Typography variant="caption">{option.label} ({option.quantity})</Typography>
                                          </Stack>
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                );
                              })()
                            ) : (
                              <Typography variant="caption" color="text.secondary">-</Typography>
                            )}
                          </TableCell>
                        )}
                        <TableCell>
                          <TextField
                            type="number"
                            value={item.quantity}
                            onChange={(e) => updateItemQuantity(item.barcode, parseInt(e.target.value) || 1)}
                            size="small"
                            inputProps={{ 
                              min: 1, 
                              max: context !== 'moveIn' && item.selectedSource ? item.selectedSource.availableQuantity : undefined,
                              style: { width: 60 } 
                            }}
                          />
                          {context !== 'moveIn' && item.selectedSource && (
                            <Typography variant="caption" display="block" color="text.secondary">
                              Max: {item.selectedSource.availableQuantity}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={isUnknown ? 'Unknown' : 'Known'}
                            size="small"
                            color={isUnknown ? 'warning' : 'success'}
                            variant="outlined"
                          />
                          {context !== 'moveIn' && item.selectedSource && (
                            <Chip
                              label="Source Set"
                              size="small"
                              color="primary"
                              variant="outlined"
                              sx={{ ml: 0.5 }}
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          <IconButton size="small" onClick={() => removeItem(item.barcode)} color="error">
                            <Delete />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
              <Inventory fontSize="large" sx={{ mb: 1 }} />
              <Typography variant="body2">No items scanned yet. Start scanning item barcodes above.</Typography>
            </Box>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleConfirm} disabled={scannedItems.length === 0}>
          Confirm Items ({scannedItems.length})
        </Button>
      </DialogActions>
      
      {/* Box Import Dialog */}
      <ExcelImportDialog
        open={boxImportDialogOpen}
        onClose={() => setBoxImportDialogOpen(false)}
        onImport={handleImportItems}
        title={`Import Items for Box ${boxBarcode}`}
        expectedColumns={{
          required: ['barcode'],
          optional: ['quantity']
        }}
      />
    </Dialog>
  );
};

// Format property names nicely
const formatPropertyName = (key: string): string => {
  const formatted = key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase())
    .trim();

  const specialCases: Record<string, string> = {
    COLOR: 'Color',
    Color: 'Color',
    Brand: 'Brand',
    MainCategory: 'Main Category',
    Category: 'Category',
    Generic: 'Generic Code',
    size: 'Size',
    gender: 'Gender',
    season: 'Season',
  };
  return specialCases[key] || formatted;
};

// Enhanced Unit Info Cell Component with dynamic property display in More Props
export const UnitInfoCell: React.FC<{ row: any }> = ({ row }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  
  if (!row.unitData?.unit?.itemAdditionalProperties && !row.unitData?.itemAdditionalProperties) {
    return <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>-</Typography>;
  }
  
  const props = row.unitData.unit?.itemAdditionalProperties || row.unitData.itemAdditionalProperties;
  const displayProps = ['Brand', 'Color', 'size', 'gender', 'Category', 'MainCategory', 'Type', 'Model', 'Description'];
  const availableProps = displayProps.filter(prop => props[prop]);
  
  if (availableProps.length === 0) {
    return <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>-</Typography>;
  }
  
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleClose = () => {
    setAnchorEl(null);
  };
  
  const open = Boolean(anchorEl);
  
  return (
    <Box
      sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        cursor: 'pointer',
        py: 0.5,
        '&:hover': { color: 'primary.main' }
      }}
      onClick={handleClick}
    >
      <Info sx={{ fontSize: '0.8rem', mr: 0.25 }} />
      <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
        {availableProps.length} properties
      </Typography>
      
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        disableEscapeKeyDown={false}
        disableAutoFocus
        disableEnforceFocus
        disableRestoreFocus
        sx={{
          '& .MuiPopover-paper': {
            boxShadow: 3,
            border: 1,
            borderColor: 'divider'
          }
        }}
      >
        <Box sx={{ p: 2, minWidth: 200, maxWidth: 300, position: 'relative' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
              Unit Properties
            </Typography>
            <IconButton 
              size="small" 
              onClick={(e) => {
                e.stopPropagation();
                handleClose();
              }}
              sx={{ 
                p: 0.5, 
                ml: 1,
                '&:hover': { backgroundColor: 'action.hover' }
              }}
            >
              <Close sx={{ fontSize: '0.8rem' }} />
            </IconButton>
          </Box>
          <Stack spacing={0.5}>
            {availableProps.map((prop) => (
              <Box key={prop} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'medium' }}>
                  {formatPropertyName(prop)}:
                </Typography>
                <Typography variant="caption" sx={{ ml: 1, wordBreak: 'break-word' }}>
                  {props[prop]}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Box>
      </Popover>
    </Box>
  );
};

// Helper hook to extract unit data from different sources
export const useUnitData = (row: any) => {
  return useMemo(() => {
    let unitData = null;
    
    // Debug log to understand the structure
    // console.log('🔍 Row data for useUnitData:', row);
    
    // Try different data source paths for unit data
    // First check for direct unit data
    if (row.unitData?.unit) {
      unitData = row.unitData.unit;
      // console.log('📍 Found unit data in row.unitData.unit');
    } else if (row.unitData) {
      unitData = row.unitData;
      // console.log('📍 Found unit data in row.unitData');
    }
    
    // Then check search result paths
    if (!unitData && row.searchResult) {
      // Check for unit data directly on searchResult (from bulk import fixes)
      if (row.searchResult.unit) {
        unitData = row.searchResult.unit;
        // console.log('📍 Found unit data in row.searchResult.unit');
      }
      // For bulk search results with nested structure
      else if (row.searchResult.result?.unit) {
        unitData = row.searchResult.result.unit;
        // console.log('📍 Found unit data in row.searchResult.result.unit');
      }
      // For bulk search results where unit data might be at result level
      else if (row.searchResult.result && (row.searchResult.result.symbol || row.searchResult.result.unitOfMeasurement || row.searchResult.result.itemAdditionalProperties)) {
        unitData = row.searchResult.result;
        // console.log('📍 Found unit data in row.searchResult.result');
      }
      // For individual search results
      else if (row.searchResult.symbol || row.searchResult.unitOfMeasurement || row.searchResult.itemAdditionalProperties) {
        unitData = row.searchResult;
        // console.log('📍 Found unit data in row.searchResult root');
      }
    }
    
    // For bulk import results, check if the result is stored differently
    if (!unitData && row.result?.unit) {
      unitData = row.result.unit;
      // console.log('📍 Found unit data in row.result.unit');
    }
    
    if (!unitData) return null;
    
    const result = {
      id: unitData.id || unitData.unitId,
      itemId: unitData.itemId,
      itemName: unitData.itemName,
      barcode: unitData.barcode || row.barcode,
      symbol: unitData.symbol,
      unitOfMeasurement: unitData.unitOfMeasurement,
      onHandQuantity: unitData.onHandQuantity,
      expectedQuantity: unitData.expectedQuantity,
      itemMinNumber: unitData.itemMinNumber,
      itemMaxNumber: unitData.itemMaxNumber,
      properties: unitData.itemAdditionalProperties || {},
      isSynced: unitData.isSynced,
      createdAt: unitData.createdAt,
      updatedAt: unitData.updatedAt
    };
    
    // Debug log to see what we extracted
    // console.log('✅ Extracted unit data:', result);
    
    return result;
  }, [row]);
};

export const CompactUnitDetails: React.FC<{ row: any }> = ({ row }) => {
  const unitData = useUnitData(row);
  
  if (!unitData || row.searchResult?.type === 'box') {
    return <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>-</Typography>;
  }
  
  return (
    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
      More properties available
    </Typography>
  );
};

// Enhanced property cell with tooltip for additional info
export const EnhancedUnitPropertyCell: React.FC<{ row: any; propertyName: string }> = ({ row, propertyName }) => {
  const unitData = useUnitData(row);
  const value = unitData?.properties?.[propertyName];
  
  if (!value) {
    return <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>-</Typography>;
  }
  
  // Special handling for colors with color swatches
  if (propertyName.toLowerCase().includes('color')) {
    const isValidColor = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$|^rgb|^hsl|^[a-zA-Z]+$/.test(value);
    
    if (isValidColor) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box
            sx={{
              width: 10,
              height: 10,
              backgroundColor: value.toLowerCase(),
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 0.5,
              flexShrink: 0
            }}
          />
          <Typography variant="caption" sx={{ fontSize: '0.65rem' }} noWrap>
            {value}
          </Typography>
        </Box>
      );
    }
  }
  
  // Category/Brand as compact chips
  if (['category', 'maincategory', 'brand', 'season'].some(f => propertyName.toLowerCase().includes(f))) {
    return (
      <Chip 
        label={value} 
        size="small" 
        variant="outlined" 
        color="primary"
        sx={{ 
          fontSize: '0.6rem', 
          height: 16, 
          '& .MuiChip-label': { 
            px: 0.5, 
            fontSize: '0.6rem' 
          } 
        }}
      />
    );
  }
  
  // Default compact text display
  return (
    <Typography variant="caption" sx={{ fontSize: '0.65rem' }} noWrap>
      {value}
    </Typography>
  );
};

export const AllPropertiesCell: React.FC<{ row: any }> = ({ row }) => {
  const unitData = useUnitData(row);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleClose = () => {
    setAnchorEl(null);
  };
  
  const open = Boolean(anchorEl);
  
  // Check if we have any data to display
  const hasSymbol = unitData?.symbol;
  const hasUnit = unitData?.unitOfMeasurement;
  const properties = unitData?.properties || {};
  const propertyEntries = Object.entries(properties).filter(([, value]) => value);
  const hasProperties = propertyEntries.length > 0;
  
  if (!hasSymbol && !hasUnit && !hasProperties) {
    return <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>-</Typography>;
  }
  
  // Prepare display lines (up to 3 lines)
  const displayLines: React.ReactNode[] = [];
  
  // Line 1: Symbol (styled like other property cells)
  if (hasSymbol) {
    displayLines.push(
      <Box key="symbol" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Box
          sx={{
            width: 10,
            height: 10,
            backgroundColor: 'primary.main',
            borderRadius: 0.5,
            flexShrink: 0
          }}
        />
        <Typography variant="caption" sx={{ 
          fontSize: '0.65rem',
          color: 'primary.main',
          fontFamily: 'monospace',
          fontWeight: 'bold'
        }}>
          {unitData.symbol}
        </Typography>
      </Box>
    );
  }
  
  // Line 2: Unit of measurement
  if (hasUnit && displayLines.length < 3) {
    displayLines.push(
      <Typography key="unit" variant="caption" sx={{ 
        fontSize: '0.6rem', 
        color: 'text.secondary',
        lineHeight: 1.1
      }} noWrap>
        Unit: {unitData.unitOfMeasurement}
      </Typography>
    );
  }
  
  // Line 3: First key property
  const keyProperties = ['Brand', 'Color', 'size', 'Category'];
  const keyProperty = propertyEntries.find(([key]) => keyProperties.includes(key));
  if (keyProperty && displayLines.length < 3) {
    const [key, value] = keyProperty;
    displayLines.push(
      <Typography key={key} variant="caption" sx={{ 
        fontSize: '0.6rem', 
        color: 'text.secondary',
        lineHeight: 1.1
      }} noWrap>
        {formatPropertyName(key)}: {String(value)}
      </Typography>
    );
  }
  
  // Calculate remaining items
  const remainingCount = propertyEntries.length - (keyProperty ? 1 : 0);
  const hasMore = remainingCount > 0 || (hasUnit && displayLines.length === 3);
  
  return (
    <Box
      sx={{ 
        py: 0.25,
        cursor: 'pointer',
        '&:hover': { 
          backgroundColor: 'action.hover',
          borderRadius: 1
        }
      }}
      onClick={(e) => {
        e.stopPropagation();
        handleClick(e);
      }}
    >
      <Stack direction="column" spacing={0.25}>
        {displayLines}
        
        {/* More indicator */}
        {hasMore && (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <MoreHoriz sx={{ fontSize: '0.7rem', mr: 0.25, color: 'text.disabled' }} />
            <Typography variant="caption" sx={{ fontSize: '0.55rem', color: 'text.disabled' }}>
              +{remainingCount + (hasUnit && displayLines.length === 3 ? 1 : 0)} more
            </Typography>
          </Box>
        )}
      </Stack>
      
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        disableEscapeKeyDown={false}
        disableAutoFocus
        disableEnforceFocus
        disableRestoreFocus
        sx={{
          '& .MuiPopover-paper': {
            boxShadow: 3,
            border: 1,
            borderColor: 'divider'
          }
        }}
      >
        <Box sx={{ p: 1.5, minWidth: 200, maxWidth: 350, position: 'relative' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>
              All Properties
            </Typography>
            <IconButton 
              size="small" 
              onClick={(e) => {
                e.stopPropagation();
                handleClose();
              }}
              sx={{ 
                p: 0.5, 
                ml: 1,
                '&:hover': { backgroundColor: 'action.hover' }
              }}
            >
              <Close sx={{ fontSize: '0.8rem' }} />
            </IconButton>
          </Box>
          <Stack spacing={0.5}>
            {/* Symbol and Unit of Measurement first */}
            {hasSymbol && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'medium', fontSize: '0.65rem' }}>
                  Symbol:
                </Typography>
                <Typography variant="caption" sx={{ fontSize: '0.65rem', wordBreak: 'break-word', fontFamily: 'monospace', fontWeight: 'bold' }}>
                  {unitData.symbol}
                </Typography>
              </Box>
            )}
            {hasUnit && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'medium', fontSize: '0.65rem' }}>
                  Unit:
                </Typography>
                <Typography variant="caption" sx={{ fontSize: '0.65rem', wordBreak: 'break-word' }}>
                  {unitData.unitOfMeasurement}
                </Typography>
              </Box>
            )}
            {/* Divider if we have symbol/unit */}
            {(hasSymbol || hasUnit) && hasProperties && (
              <Divider sx={{ my: 0.5 }} />
            )}
            {/* Other properties */}
            {propertyEntries.map(([key, value]) => (
              <Box key={key} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'medium', fontSize: '0.65rem' }}>
                  {formatPropertyName(key)}:
                </Typography>
                <Typography variant="caption" sx={{ fontSize: '0.65rem', wordBreak: 'break-word' }}>
                  {String(value)}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Box>
      </Popover>
    </Box>
  );
};

// Create specific property cell components
export const UnitPropertyCell: React.FC<{ row: any; propertyName: string }> = ({ row, propertyName }) => {
  const unitData = useUnitData(row);
  const value = unitData?.properties?.[propertyName];
  
  if (!value) {
    return <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>-</Typography>;
  }
  
  // Special handling for colors
  if (propertyName.toLowerCase().includes('color')) {
    // Simple color detection - could be enhanced
    const isValidColor = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$|^rgb|^hsl|^[a-zA-Z]+$/.test(value);
    
    if (isValidColor) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box
            sx={{
              width: 12,
              height: 12,
              backgroundColor: value.toLowerCase(),
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 0.5,
            }}
          />
          <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>{value}</Typography>
        </Box>
      );
    }
  }
  
  // Category/Brand as chips
  if (['category', 'maincategory', 'brand', 'season'].some(f => propertyName.toLowerCase().includes(f))) {
    return (
      <Chip 
        label={value} 
        size="small" 
        variant="outlined" 
        color="primary"
        sx={{ fontSize: '0.65rem', height: 18 }}
      />
    );
  }
  
  // Default text display
  return (
    <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
      {value}
    </Typography>
  );
};

// Enhanced Type & Details Cell Component
export const TypeDetailsCell: React.FC<{
  row: any;
  onBoxClick?: (rowId: string, boxBarcode: string, boxDetails: BoxDetails) => void;
}> = ({ row, onBoxClick }) => {
  if (row.searchResult || (row.type === 'box' && row.boxDetails)) {
    const isBox = row.type === 'box' || row.searchResult?.type === 'box';
    const isGeneratedBox = isBox && (row.searchResult as any)?.isGenerated === true;
    const boxDetails = row.boxDetails || (row.searchResult as any)?.boxItems;
    const itemCount = boxDetails?.length || (row.boxDetails?.items?.length) || 0;
    const totalQuantity = boxDetails?.reduce?.((sum: number, item: any) => sum + (item.quantity || 0), 0) || 
                         (row.boxDetails?.items?.reduce?.((sum: number, item: any) => sum + item.quantity, 0)) || 0;
    
    if (isBox) {
      return (
        <Stack direction="column" spacing={0.25} sx={{ py: 0.5 }}>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Chip 
              label={isGeneratedBox ? "NEW" : "BOX"} 
              size="small" 
              color="primary" 
              variant={isGeneratedBox ? "filled" : "outlined"}
              sx={{ fontSize: '0.6rem', height: 16 }}
            />
            <Typography 
              variant="body2" 
              sx={{ 
                cursor: isGeneratedBox ? 'pointer' : 'default',
                '&:hover': isGeneratedBox ? { textDecoration: 'underline' } : {},
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontSize: '0.75rem',
                fontWeight: 500
              }}
              onClick={isGeneratedBox && onBoxClick ? () => onBoxClick(row.id, row.barcode, {
                barcode: row.barcode,
                storageUnitId: '',
                storageUnitName: 'Generated Box',
                items: boxDetails || []
              }) : undefined}
              title={isGeneratedBox ? `${row.barcode} - ${itemCount} items - Click to manage` : `${row.barcode} - ${itemCount} items`}
            >
              {row.barcode}
            </Typography>
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
            {itemCount} items {totalQuantity > 0 && `• ${totalQuantity} qty`}
          </Typography>
        </Stack>
      );
    } else {
      // Item type
      const isUnknown = row.error;
      const itemName = row.itemName || row.searchResult?.itemName || 'Unknown Item';
      const unitData = useUnitData(row);
      
      return (
        <Stack direction="column" spacing={0.25} sx={{ py: 0.5 }}>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Chip 
              label="ITEM" 
              size="small" 
              color="secondary"
              variant="outlined"
              sx={{ fontSize: '0.6rem', height: 16 }}
            />
            {isUnknown && <Chip label="UNK" size="small" color="warning" sx={{ fontSize: '0.6rem', height: 16 }} />}
          </Stack>
          <Typography 
            variant="caption" 
            sx={{ 
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontSize: '0.75rem',
              fontWeight: 500,
              lineHeight: 1.2
            }}
            title={itemName}
          >
            {itemName}
          </Typography>
        </Stack>
      );
    }
  }
  return <Typography variant="body2" color="text.secondary">-</Typography>;
};